import { useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import GrowLabLogo from '../components/GrowLabLogo.jsx'
import ChatDemo from '../components/ChatDemo.jsx'
import TestimonialsScroll from '../components/TestimonialsScroll.jsx'

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
    <path d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
    <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.859-3.048.859-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
    <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
    <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
  </svg>
)

export default function Login() {
  const { t } = useTranslation()
  const canvasRef = useRef(null)
  const googleBtnRef = useRef(null)

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const ref = params.get('ref')
    const invite = params.get('invite')
    if (ref) sessionStorage.setItem('pending_ref', ref)
    if (invite) sessionStorage.setItem('pending_invite', invite)
  }, [])

  useEffect(() => {
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    let animId

    const resize = () => {
      canvas.width  = window.innerWidth
      canvas.height = window.innerHeight
    }
    resize()
    window.addEventListener('resize', resize)

    const particles = Array.from({ length: 55 }, () => ({
      x:       Math.random() * canvas.width,
      y:       Math.random() * canvas.height,
      r:       Math.random() * 2 + 0.5,
      speed:   Math.random() * 0.4 + 0.15,
      opacity: Math.random() * 0.5 + 0.1,
      drift:   (Math.random() - 0.5) * 0.3,
      color:   Math.random() > 0.7 ? '#00FFFF' : '#1DB954'
    }))

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height)

      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx   = particles[i].x - particles[j].x
          const dy   = particles[i].y - particles[j].y
          const dist = Math.sqrt(dx * dx + dy * dy)
          if (dist < 55) {
            ctx.beginPath()
            ctx.moveTo(particles[i].x, particles[i].y)
            ctx.lineTo(particles[j].x, particles[j].y)
            ctx.strokeStyle = '#1DB954'
            ctx.globalAlpha = (1 - dist / 55) * 0.12
            ctx.lineWidth   = 0.5
            ctx.stroke()
            ctx.globalAlpha = 1
          }
        }
      }

      particles.forEach(p => {
        ctx.beginPath()
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2)
        ctx.fillStyle   = p.color
        ctx.globalAlpha = p.opacity
        ctx.fill()
        ctx.globalAlpha = 1

        p.y -= p.speed
        p.x += p.drift
        if (p.y < -4) {
          p.y = canvas.height + 4
          p.x = Math.random() * canvas.width
        }
        if (p.x < -4 || p.x > canvas.width + 4) {
          p.x = Math.random() * canvas.width
        }
      })

      animId = requestAnimationFrame(draw)
    }
    draw()

    return () => {
      cancelAnimationFrame(animId)
      window.removeEventListener('resize', resize)
    }
  }, [])

  function handleGoogleClick() {
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID
    const redirectUri = import.meta.env.VITE_REDIRECT_URI ?? 'https://growlab.cloud/auth/callback'
    window.location.href =
      `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}` +
      `&redirect_uri=${encodeURIComponent(redirectUri)}` +
      `&response_type=code&scope=openid%20email%20profile&prompt=select_account`
  }

  return (
    <div className="login-page">
      <canvas ref={canvasRef} className="login-canvas" />
      <nav className="landing-navbar">
        <div className="landing-navbar-brand">
          <GrowLabLogo size={22} />
          <span className="login-topbar-name">GROWLAB CLOUD</span>
        </div>
      </nav>
      <div className="landing-hero-grid">
        <div className="landing-hero-left">
          <h1 className="landing-h1">{t('login.heroH1')}</h1>
          <h2 className="landing-h2">{t('login.heroH2')}</h2>
          <p className="landing-subtext">{t('login.heroSubtext')}</p>
          <div className="login-stats login-stats--panel">
            <div className="login-stat-item">
              <span className="login-stat-num">100+</span>
              <span className="login-stat-label">{t('login.statBooks')}</span>
            </div>
            <div className="login-stat-item">
              <span className="login-stat-num">{t('login.stat1AI')}</span>
            </div>
            <div className="login-stat-item">
              <span className="login-stat-num">{t('login.statRag')}</span>
              <span className="login-stat-label">{t('login.statPowered')}</span>
            </div>
          </div>
          <button ref={googleBtnRef} className="google-btn google-btn--hero" onClick={handleGoogleClick}>
            <GoogleIcon />
            Start for free with Google →
          </button>
          <p className="login-browsewrap">
            {t('login.browsewrap')}{' '}
            <Link to="/terms" className="login-legal-link">{t('legal.termsOfService')}</Link>
            {' '}{t('login.browsewrapAnd')}{' '}
            <Link to="/privacy" className="login-legal-link">{t('legal.privacyPolicy')}</Link>
          </p>
          <hr className="openai-badge-divider" />
          <div className="openai-badge">⚡ Powered by OpenAI</div>
        </div>
        <div className="landing-hero-right">
          <ChatDemo />
        </div>
      </div>
      <section className="landing-testimonials-section">
        <TestimonialsScroll />
      </section>
      <footer className="landing-footer">
        <div className="landing-footer-brand">
          <GrowLabLogo size={20} />
          <span>GROWLAB CLOUD</span>
        </div>
        <div className="landing-footer-links">
          <Link to="/terms">Terms of Service</Link>
          <span>·</span>
          <Link to="/privacy">Privacy Policy</Link>
        </div>
        <div className="landing-footer-openai">⚡ Powered by OpenAI</div>
      </footer>
    </div>
  )
}
